/**
 * Servicio de Proveedores RPC
 * Maneja las conexiones a las blockchains
 */

import { ethers } from 'ethers';
import config from '@/config';

// Cache de proveedores
const providers: Record<string, ethers.Provider> = {};

/**
 * Servicio de Proveedores
 */
export const providerService = {
  /**
   * Obtiene un proveedor para una red específica
   */
  getProvider(networkId: string): ethers.Provider | null {
    // Si ya existe en cache, retornarlo
    if (providers[networkId]) {
      return providers[networkId];
    }

    // Obtener configuración de la red
    const networkConfig = config.getNetwork(networkId);
    if (!networkConfig) {
      console.error(`Red no encontrada: ${networkId}`);
      return null;
    }

    try {
      let provider: ethers.Provider | null = null;

      // USAR SOLO HTTP (WebSocket da problemas de timeout)
      try {
        const network = new ethers.Network(networkConfig.name, networkConfig.chainId);

        // Si la URL es relativa (proxy), hacerla absoluta para ethers.js
        let rpcUrl = networkConfig.rpcUrl;
        if (rpcUrl.startsWith('/')) {
          rpcUrl = window.location.origin + rpcUrl;
          console.log(`[Provider] Usando URL absoluta para proxy: ${rpcUrl}`);
        }

        // Configurar petición con timeout
        const fetchReq = new ethers.FetchRequest(rpcUrl);
        // OMDB pasa por proxy, necesita más tiempo
        const timeoutMs = (networkId === 'omdb' || networkId === 'omdb-local') ? 10000 : 5000;
        fetchReq.timeout = timeoutMs;

        // Deshabilitar los reintentos automáticos de ethers (que causan cuelgues)
        fetchReq.retryFunc = async () => {
          console.warn(`[Provider] Evitando reintento automático para ${networkId}`);
          return false; // Nunca reintentar
        };

        // Configuración con timeout para evitar cuelgues infinitos
        provider = new ethers.JsonRpcProvider(fetchReq, network, {
          staticNetwork: network,
          batchMaxCount: 1
        });

        console.log(`✅ Proveedor HTTP creado para ${networkId}: ${networkConfig.rpcUrl}`);
      } catch (httpError) {
        console.warn(`❌ Error creando HTTP provider para ${networkId}:`, httpError);

        // Proveedor fallback que retorna valores por defecto
        provider = this.createFallbackProvider();
        console.log(`⚠️ Proveedor fallback creado para ${networkId}`);
      }

      // Guardar en cache
      if (provider) {
        providers[networkId] = provider;
      }

      return provider;
    } catch (error) {
      console.error(`❌ Error al crear proveedor para ${networkId}:`, error);
      return this.createFallbackProvider();
    }
  },

  /**
   * Crea un proveedor fallback que retorna valores por defecto
   */
  createFallbackProvider(): any {
    return {
      _isFallback: true,
      getBalance: async () => ethers.parseEther('0'),
      getBlockNumber: async () => 0,
      getTransactionCount: async () => 0,
      getTransactionReceipt: async () => null,
      getBlock: async () => null,
      getCode: async () => '0x',
      call: async () => '0x',
      estimateGas: async () => ethers.parseUnits('21000', 'wei'),
      getGasPrice: async () => ethers.parseUnits('20', 'gwei'),
      getFeeData: async () => ({
        gasPrice: ethers.parseUnits('20', 'gwei'),
        maxFeePerGas: null,
        maxPriorityFeePerGas: null,
      }),
      on: () => { },
      off: () => { },
      removeAllListeners: () => { },
    };
  },

  /**
   * Limpia el cache de proveedores
   */
  clearProviders(): void {
    Object.keys(providers).forEach((key) => {
      const provider = providers[key];

      // Cerrar WebSocket si existe
      if ((provider as any)._websocket) {
        try {
          (provider as any)._websocket.close();
        } catch (e) {
          console.error('Error cerrando WebSocket:', e);
        }
      }

      delete providers[key];
    });

    console.log('[Provider] Cache de proveedores limpiado');
  },

  /**
   * Limpia el proveedor de una red específica
   */
  clearProvider(networkId: string): void {
    if (providers[networkId]) {
      const provider = providers[networkId];

      // Cerrar WebSocket si existe
      if ((provider as any)._websocket) {
        try {
          (provider as any)._websocket.close();
        } catch (e) {
          console.error('Error cerrando WebSocket:', e);
        }
      }

      delete providers[networkId];
      console.log(`[Provider] Proveedor de ${networkId} limpiado`);
    }
  },

  /**
   * Verifica si un proveedor es fallback
   */
  isFallbackProvider(provider: any): boolean {
    return provider?._isFallback === true;
  },

  /**
   * Obtiene el número de bloque actual
   */
  async getBlockNumber(networkId: string): Promise<number> {
    try {
      const provider = this.getProvider(networkId);
      if (!provider || this.isFallbackProvider(provider)) {
        return 0;
      }

      return await provider.getBlockNumber();
    } catch (error) {
      console.error(`Error obteniendo block number para ${networkId}:`, error);
      return 0;
    }
  },

  /**
   * Obtiene el gas price actual
   */
  async getGasPrice(networkId: string): Promise<bigint> {
    try {
      const provider = this.getProvider(networkId);
      if (!provider || this.isFallbackProvider(provider)) {
        return ethers.parseUnits('20', 'gwei');
      }

      const feeData = await provider.getFeeData();
      return feeData.gasPrice || ethers.parseUnits('20', 'gwei');
    } catch (error) {
      console.error(`Error obteniendo gas price para ${networkId}:`, error);
      return ethers.parseUnits('20', 'gwei');
    }
  },
};

export default providerService;
